import { useEffect, useRef, useState, type KeyboardEvent } from "react";

import { cn } from "@/lib/cn";
import { closestDirection, horizontalStep } from "@/lib/textDirection";
import { Tooltip } from "@/ui/Tooltip";

/**
 * A generic, accessible tab strip for the Lab: one tablist, roving tabindex
 * (the selected tab is the only tab stop), arrows that follow the rendered
 * direction, Home / End, and focus that moves with the selection. The strip
 * scrolls horizontally inside its own box, so it works at phone width.
 *
 * Controlled: it holds no state and never touches the router; the caller owns
 * the selection (the Lab keeps it in the address as an opaque id).
 */
export function LabTabStrip<T extends string>({
  idPrefix,
  ariaLabel,
  tabs,
  value,
  onChange,
  panelId,
}: {
  idPrefix: string;
  ariaLabel: string;
  /** `help`: one plain sentence, shown as a tooltip on hover and focus. */
  tabs: readonly { id: T; label: string; help?: string }[];
  value: T;
  onChange: (id: T) => void;
  /** The one panel every tab controls; defaults to `${idPrefix}-panel`. */
  panelId?: string;
}) {
  const controls = panelId ?? `${idPrefix}-panel`;
  const list = useRef<HTMLDivElement>(null);
  // Which edges have more tabs beyond them (drives the fade + arrow hints).
  const [more, setMore] = useState({ start: false, end: false });

  useEffect(() => {
    const el = list.current;
    if (el === null) {
      return;
    }
    const measure = () => {
      const max = el.scrollWidth - el.clientWidth;
      // scrollLeft is negative in RTL; the sign only says which side is which.
      const at = Math.abs(el.scrollLeft);
      const next = { start: at > 1, end: max - at > 1 };
      setMore((cur) =>
        cur.start === next.start && cur.end === next.end ? cur : next,
      );
    };
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(measure);
    observer?.observe(el);
    return () => {
      el.removeEventListener("scroll", measure);
      observer?.disconnect();
    };
  }, [tabs.length]);

  // Keep the selected tab in view (e.g. after a keyboard move or a link).
  useEffect(() => {
    document
      .getElementById(`${idPrefix}-${value}`)
      ?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [idPrefix, value]);

  function nudge(direction: 1 | -1) {
    const el = list.current;
    if (el !== null) {
      const rtl = getComputedStyle(el).direction === "rtl";
      el.scrollBy?.({
        left: direction * (rtl ? -1 : 1) * Math.max(120, el.clientWidth / 2),
        behavior: "smooth",
      });
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    const index = tabs.findIndex((tab) => tab.id === value);
    let next: number;
    if (event.key === "Home") {
      next = 0;
    } else if (event.key === "End") {
      next = tabs.length - 1;
    } else {
      const step = horizontalStep(
        event.key,
        closestDirection(event.currentTarget),
      );
      if (step === 0) {
        return;
      }
      next = (index + step + tabs.length) % tabs.length;
    }
    event.preventDefault();
    const target = tabs[next];
    if (target) {
      onChange(target.id);
      document.getElementById(`${idPrefix}-${target.id}`)?.focus();
    }
  }

  return (
    <div className="relative">
      <div
        ref={list}
        role="tablist"
        aria-label={ariaLabel}
        className="flex overflow-x-auto border-b border-border [scrollbar-width:thin]"
      >
        {tabs.map((tab) => {
          const button = (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`${idPrefix}-${tab.id}`}
              aria-selected={value === tab.id}
              aria-controls={controls}
              tabIndex={value === tab.id ? 0 : -1}
              className={cn(
                "-mb-px shrink-0 border-b-2 px-3 py-2 text-sm whitespace-nowrap focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
                value === tab.id
                  ? "border-primary font-medium text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
              onClick={() => onChange(tab.id)}
              onKeyDown={onKeyDown}
            >
              {tab.label}
            </button>
          );
          return tab.help === undefined ? (
            button
          ) : (
            <Tooltip key={tab.id} content={tab.help}>
              {button}
            </Tooltip>
          );
        })}
      </div>
      {(["start", "end"] as const).map((edge) =>
        more[edge] ? (
          <button
            key={edge}
            type="button"
            tabIndex={-1}
            aria-hidden="true"
            data-testid={`tabstrip-more-${edge}`}
            onClick={() => nudge(edge === "end" ? 1 : -1)}
            className={cn(
              "absolute inset-y-0 flex w-8 items-center text-muted-foreground",
              edge === "start"
                ? "start-0 justify-start bg-gradient-to-r from-background to-transparent rtl:bg-gradient-to-l"
                : "end-0 justify-end bg-gradient-to-l from-background to-transparent rtl:bg-gradient-to-r",
            )}
          >
            <span className="rtl:hidden">{edge === "start" ? "‹" : "›"}</span>
            <span className="hidden rtl:inline">
              {edge === "start" ? "›" : "‹"}
            </span>
          </button>
        ) : null,
      )}
    </div>
  );
}
